import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { httpService } from '../../../../../utility/httpService';
import { httpConstants } from '../../../../../constants';
import { API_BASE } from '../../../../../config/env';
//import { API_BASE } from '../../../../../config/env';

interface VersioningManagerProps {
  currentStep: number;
  ideationId: string | null;
}

export interface ActiveSubStep {
  step_name: 'process_diagram' | 'ai_opportunity' | 'opportunity' | '';
  opportunity_id?: string;
}

/**
 * Utility function to trigger an ideation version creation.
 * Can be used for manual triggers from other components.
 * @param activeSubStep - optional sub-step context (e.g. process_diagram) to tag the snapshot
 */
export const createIdeationVersion = async (
  ideationId: string,
  type: string = 'manual',
  activeSubStep?: ActiveSubStep
) => {
  if (!ideationId) return;
  try {
    const url = `${API_BASE}/api/v1/ideation-version/create/`;
    const payload: Record<string, unknown> = {
      ideation: ideationId,
      version_type: type,
    };
    if (activeSubStep) {
      payload.active_sub_step = activeSubStep;
    }
    await httpService(httpConstants.METHOD_TYPE.POST, url, payload);
  } catch (error) {
    console.error('Error creating ideation version:', error);
  }
};

/**
 * Component that manages automatic versioning triggers for Ideation.
 * It does not render anything (returns null).
 */
const VersioningManager: React.FC<VersioningManagerProps> = ({ currentStep, ideationId }) => {
  const formState = useSelector((state: RootState) => state.newIdeation);

  // Trigger version creation every 30 seconds if on Step 0 or 1
  useEffect(() => {
    if (!ideationId || (currentStep !== 0 && currentStep !== 1)) return;

    const intervalId = setInterval(() => {
      createIdeationVersion(ideationId, 'manual');
    }, 60000);

    return () => clearInterval(intervalId);
  }, [ideationId, currentStep]);

  // Trigger version creation on field changes in Step 0
  useEffect(() => {
    if (currentStep !== 0 || !ideationId) return;

    const handler = setTimeout(() => {
      createIdeationVersion(ideationId, 'manual');
    }, 2000);

    return () => clearTimeout(handler);
  }, [
    formState.domain,
    formState.selectedFunction,
    formState.selectedProcessGroup,
    formState.selectedProcess,
    formState.currentProcess,
    formState.expectedImpact,
    formState.specificIdeasOfImprovement,
    formState.keyPerformanceIndicators,
    formState.technologies,
    currentStep,
    ideationId,
  ]);

  return null;
};

export default VersioningManager;
